/**
 * ContentHubItemEdit Page
 * Provides a form to edit an existing content item
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertCircle,
  FileText,
  Hash,
  Image as ImageIcon,
  Table as TableIcon,
  Mail,
  Presentation,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { FluentCard } from '@/components/layout/page-header'
import { useToast } from '@/components/ui/use-toast'
import {
  CommonFieldsForm,
  TypeSpecificEditor,
  type CommonFieldsFormData,
  type TypeSpecificContent,
} from '@/components/content-hub'
import { useContentItem, useUpdateContentItem } from '@/lib/hooks/use-content-hub'
import type {
  ContentItemType,
  UpdateContentItemRequest,
  TextElementContent,
  DataPointContent,
  EmailSnippetContent,
} from '@/lib/api/types'

// ============================================
// Types & Constants
// ============================================

const TYPE_ICONS: Record<ContentItemType, React.ElementType> = {
  Text: FileText,
  DataPoint: Hash,
  Image: ImageIcon,
  Table: TableIcon,
  EmailSnippet: Mail,
  SlideElement: Presentation,
}

const TYPE_COLORS: Record<ContentItemType, string> = {
  Text: 'from-blue-400 to-blue-600',
  DataPoint: 'from-green-400 to-green-600',
  Image: 'from-purple-400 to-purple-600',
  Table: 'from-amber-400 to-amber-600',
  EmailSnippet: 'from-rose-400 to-rose-600',
  SlideElement: 'from-orange-400 to-orange-600',
}

const TYPE_LABELS: Record<ContentItemType, string> = {
  Text: 'Text Element',
  DataPoint: 'Data Point',
  Image: 'Image',
  Table: 'Table',
  EmailSnippet: 'Email Snippet',
  SlideElement: 'Slide Element',
}

// ============================================
// Loading State Component
// ============================================

function LoadingState() {
  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="h-6 w-40 bg-neutral-200 dark:bg-neutral-700 rounded animate-pulse" />
      <div className="flex items-center gap-4">
        <div className="h-14 w-14 rounded-2xl bg-neutral-200 dark:bg-neutral-700 animate-pulse" />
        <div className="flex-1 space-y-2">
          <div className="h-8 w-48 bg-neutral-200 dark:bg-neutral-700 rounded animate-pulse" />
          <div className="h-4 w-64 bg-neutral-200 dark:bg-neutral-700 rounded animate-pulse" />
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <div className="h-48 bg-neutral-200 dark:bg-neutral-700 rounded-xl animate-pulse" />
          <div className="h-64 bg-neutral-200 dark:bg-neutral-700 rounded-xl animate-pulse" />
        </div>
        <div className="space-y-6">
          <div className="h-32 bg-neutral-200 dark:bg-neutral-700 rounded-xl animate-pulse" />
          <div className="h-48 bg-neutral-200 dark:bg-neutral-700 rounded-xl animate-pulse" />
        </div>
      </div>
    </div>
  )
}

// ============================================
// Error State Component
// ============================================

interface ErrorStateProps {
  message: string
}

function ErrorState({ message }: ErrorStateProps) {
  const navigate = useNavigate()

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <Link
        to="/content-hub"
        className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Content Hub
      </Link>

      <FluentCard className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
          Error Loading Content
        </h2>
        <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">{message}</p>
        <Button onClick={() => navigate('/content-hub')} className="mt-4 rounded-full">
          Go to Content Hub
        </Button>
      </FluentCard>
    </div>
  )
}

// ============================================
// Helper Functions
// ============================================

function extractTypeSpecificContent(
  item: {
    type: ContentItemType
    textContent?: TextElementContent
    dataPointContent?: DataPointContent
    emailSnippetContent?: EmailSnippetContent
  } | null
): TypeSpecificContent {
  if (!item) return null

  switch (item.type) {
    case 'Text':
      return item.textContent || null
    case 'DataPoint':
      return item.dataPointContent || null
    case 'EmailSnippet':
      return item.emailSnippetContent || null
    default:
      return null
  }
}

function buildUpdateRequest(
  type: ContentItemType,
  commonFields: CommonFieldsFormData,
  typeContent: TypeSpecificContent
): UpdateContentItemRequest {
  // Backend uses polymorphic JSON serialization - contentType must be first property
  // and type-specific fields go directly on the root object
  const baseRequest = {
    contentType: type === 'Text' ? 'TextElement' : type === 'DataPoint' ? 'DataPoint' : type,
    name: commonFields.name,
    description: commonFields.description || undefined,
    tags: commonFields.tags,
    isActive: commonFields.isActive,
  }

  // Add type-specific content based on content type
  switch (type) {
    case 'Text':
      if (typeContent) {
        const textContent = typeContent as Partial<TextElementContent>
        return {
          ...baseRequest,
          content: textContent.content || '',
        } as UpdateContentItemRequest
      }
      break
    case 'DataPoint':
      if (typeContent) {
        const dpContent = typeContent as Partial<DataPointContent>
        return {
          ...baseRequest,
          value: dpContent.currentValue || '',
          dataPointType: dpContent.valueType || 'Text',
          format: dpContent.format,
        } as UpdateContentItemRequest
      }
      break
    case 'EmailSnippet':
      if (typeContent) {
        const emailContent = typeContent as Partial<EmailSnippetContent>
        return {
          ...baseRequest,
          subject: emailContent.subject,
          body: emailContent.body || '',
          isHtml: emailContent.bodyFormat === 'Html',
        } as UpdateContentItemRequest
      }
      break
  }

  return baseRequest as UpdateContentItemRequest
}

// ============================================
// Main Component
// ============================================

export default function ContentHubItemEdit() {
  const { itemId } = useParams<{ itemId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  // Form state
  const [commonFields, setCommonFields] = useState<CommonFieldsFormData>({
    name: '',
    description: '',
    folderId: null,
    tags: [],
    isActive: true,
  })
  const [typeContent, setTypeContent] = useState<TypeSpecificContent>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<keyof CommonFieldsFormData, string>>>({})

  // Fetch item data
  const {
    data: item,
    isLoading: itemLoading,
    error: itemError,
  } = useContentItem(itemId || '', {
    enabled: !!itemId,
  })

  // Update mutation
  const updateItem = useUpdateContentItem({
    onSuccess: () => {
      setHasChanges(false)
      toast({
        title: 'Content updated',
        description: 'Your changes have been saved successfully.',
      })
      navigate(`/content-hub/item/${itemId}`)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update content item',
        variant: 'destructive',
      })
    },
  })

  // Initialize form with item data
  useEffect(() => {
    if (item) {
      setCommonFields({
        name: item.name,
        description: item.description || '',
        folderId: item.folderId || null,
        tags: item.tags || [],
        isActive: item.isActive,
      })
      setTypeContent(extractTypeSpecificContent(item))
    }
  }, [item])

  // Track changes
  const handleCommonFieldsChange = useCallback((data: CommonFieldsFormData) => {
    setCommonFields(data)
    setHasChanges(true)
    // Clear name error when user starts typing
    if (data.name.trim() && errors.name) {
      setErrors((prev) => ({ ...prev, name: undefined }))
    }
  }, [errors.name])

  const handleTypeContentChange = useCallback((content: TypeSpecificContent) => {
    setTypeContent(content)
    setHasChanges(true)
  }, [])

  // Unsaved changes warning (browser close/refresh)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasChanges])

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CommonFieldsFormData, string>> = {}

    if (!commonFields.name.trim()) {
      newErrors.name = 'Name is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors before saving',
        variant: 'destructive',
      })
      return
    }

    if (!item) return

    const request = buildUpdateRequest(item.type, commonFields, typeContent)
    updateItem.mutate({ id: itemId!, data: request })
  }

  // Handle cancel
  const handleCancel = () => {
    navigate(`/content-hub/item/${itemId}`)
  }

  // Loading state
  if (itemLoading) {
    return <LoadingState />
  }

  // Error state
  if (itemError || !item) {
    return <ErrorState message={itemError?.message || 'Content item not found'} />
  }

  const TypeIcon = TYPE_ICONS[item.type] || FileText
  const typeColor = TYPE_COLORS[item.type] || 'from-gray-400 to-gray-600'
  const typeLabel = TYPE_LABELS[item.type] || item.type

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Back Button */}
      <Link
        to={`/content-hub/item/${itemId}`}
        className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Item
      </Link>

      {/* Page Header */}
      <div className="flex items-center gap-4">
        <div className={cn('p-3 rounded-2xl bg-gradient-to-br shadow-lg', typeColor)}>
          <TypeIcon className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
            Edit {typeLabel}
          </h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">
            Update the content and settings
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Type-Specific Editor */}
            <TypeSpecificEditor
              type={item.type}
              value={typeContent}
              onChange={handleTypeContentChange}
              disabled={updateItem.isPending}
            />
          </div>

          {/* Sidebar - Common Fields */}
          <div className="space-y-0">
            <CommonFieldsForm
              value={commonFields}
              onChange={handleCommonFieldsChange}
              errors={errors}
              disabled={updateItem.isPending}
              showActiveToggle
            />

            {/* Content Type Info */}
            <FluentCard className="!mt-6 bg-neutral-50 dark:bg-neutral-800/50">
              <div className="flex items-center gap-3 mb-3">
                <div className={cn('p-2 rounded-xl bg-gradient-to-br', typeColor)}>
                  <TypeIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-neutral-900 dark:text-white">{typeLabel}</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">Content Type</p>
                </div>
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                The content type cannot be changed after creation.
              </p>
            </FluentCard>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-800">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={updateItem.isPending}
            className="rounded-full"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={updateItem.isPending || !hasChanges}
            className="rounded-full gap-2 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 hover:bg-neutral-800 dark:hover:bg-neutral-100"
          >
            {updateItem.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </form>

    </div>
  )
}
